import {Grid} from "@mui/material";
import {CopyUtils} from "../../../util/CopyUtils";
import {SubmissionTranslKey} from "../SubmissionTranslKey";
import {useTranslation} from "react-i18next";
import {
    SubmissionAttachmentType,
    SubmissionFilterDto,
    SubmissionStateType
} from "../../../client/submission/SubmissionApiClient";
import TextField from "../../../common/form/TextField";
import DateField from "../../../common/form/DateField";
import MultiSelect from "../../../common/form/MultiSelect";
import {SubmissionFormatter} from "./SubmissionFormatter";
import {DateTimeFormatter} from "../../../common/DateTimeFormatter";

export interface SubmissionFilterFormProps {
    filter: SubmissionFilterDto;
    isAdminOverview: boolean;
    onChange: (filter: SubmissionFilterDto) => void;
}

export function SubmissionFilterForm(props: Readonly<SubmissionFilterFormProps>) {
    const {filter, isAdminOverview, onChange} = props;

    const {t} = useTranslation();

    function onSubmissionNumberChange(submissionNumber: string) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.submissionNumberLike = normalizeString(submissionNumber);
        onChange(updatedFilter);
    }

    function onSubmittedAtFromChange(submittedAtFrom: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.submittedAtFrom = submittedAtFrom;
        onChange(updatedFilter);
    }

    function onSubmittedAtToChange(submittedAtTo: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.submittedAtTo = DateTimeFormatter.convertToEndOfDay(submittedAtTo);
        onChange(updatedFilter);
    }

    function onStateUpdatedAtFromChange(stateUpdatedAtFrom: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.stateUpdatedAtFrom = stateUpdatedAtFrom;
        onChange(updatedFilter);
    }

    function onStateUpdatedAtToChange(stateUpdatedAtTo: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.stateUpdatedAtTo = DateTimeFormatter.convertToEndOfDay(stateUpdatedAtTo);
        onChange(updatedFilter);
    }

    function onSubmissionStateTypeChange(stateTypes: string[]) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.submissionStateTypeCodes = stateTypes as SubmissionStateType[];
        onChange(updatedFilter);
    }

    const submissionStateTypeOptions =
        Object.values(SubmissionStateType).map(state => ({
        value: state,
        label: SubmissionFormatter.formatSubmissionStateType(state, t),
    }));

    function onSubmissionAttachmentTypeChange(stateTypes: string[]) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.hasNotSubmissionAttachmentTypes = stateTypes as SubmissionAttachmentType[];
        onChange(updatedFilter);
    }

    const submissionAttachmentTypeOptions =
        Object.values(SubmissionAttachmentType).map(state => ({
            value: state,
            label: SubmissionFormatter.formatSubmissionAttachmentType(state, t),
        }));

    function normalizeString(value: string): string | undefined {
        const normalizedValue = value.trim();
        return (normalizedValue.length > 0) ? normalizedValue : undefined;
    }

    return (
        <Grid container mt={0} spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    label={t(SubmissionTranslKey.DOCUMENT_NUMBER)}
                    value={filter.submissionNumberLike}
                    onChange={onSubmissionNumberChange}
                />
            </Grid>
            {isAdminOverview && (
                <>
                    <Grid item xs={12} sm={6} md={3}>
                        <DateField
                            label={t(SubmissionTranslKey.SUBMISSIONS_SUBMITTED_AT_FROM)}
                            value={filter.submittedAtFrom}
                            onChange={onSubmittedAtFromChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DateField
                            label={t(SubmissionTranslKey.SUBMISSIONS_SUBMITTED_AT_TO)}
                            value={filter.submittedAtTo}
                            onChange={onSubmittedAtToChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DateField
                            label={t(SubmissionTranslKey.SUBMISSIONS_STATE_UPDATED_FROM)}
                            value={filter.stateUpdatedAtFrom}
                            onChange={onStateUpdatedAtFromChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DateField
                            label={t(SubmissionTranslKey.SUBMISSIONS_STATE_UPDATED_TO)}
                            value={filter.stateUpdatedAtTo}
                            onChange={onStateUpdatedAtToChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MultiSelect label={t(SubmissionTranslKey.DELIVERY_CURRENT_STATE_TYPE)}
                                     onChange={onSubmissionStateTypeChange}
                                     options={submissionStateTypeOptions}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MultiSelect label={t(SubmissionTranslKey.SUBMISSIONS_MISSING_ATTACHMENT_TYPES)}
                                     onChange={onSubmissionAttachmentTypeChange}
                                     options={submissionAttachmentTypeOptions}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
}
